import { Link } from "react-router-dom";
import { Duplicates } from "@/components/customer/Duplicates";
import { StatusPill, initialsOf } from "@/components/patterns";
import { ROLE_LABEL, ROLE_TONE } from "@/lib/buyingRoles";
import { formatINRCompact } from "@/lib/utils";
import type { AccountRecord, DealRecord } from "@/types";
import type { Task } from "@/types/task";
import { dateLocale, withClock } from "@/lib/dateTime";

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3.5 py-3">
    <h2 className="mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
    {children}
  </section>
);
const when = (d: string) => new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));
const fullName = (c: { first_name: string; last_name?: string }) => `${c.first_name} ${c.last_name || ""}`.trim();

/** The company, the deals they are on and in what role, what is due next, and others at the same company */
export function ContactRelated({
  contactId,
  account,
  rollup,
  deals,
  tasks,
  colleagues,
  can,
  onTask,
  onDoneTask,
  onMerged,
}: {
  contactId: string;
  account?: AccountRecord | null;
  rollup?: { open_deals: number; open_value: number };
  deals: DealRecord[];
  tasks: Task[];
  colleagues: { id: string; first_name: string; last_name?: string; job_title?: string; is_primary?: boolean }[];
  can: { task: boolean; merge: boolean };
  onTask: () => void;
  onDoneTask: (t: Task) => void;
  onMerged: () => void;
}) {
  const due = tasks.filter((t) => t.task_status === "OPEN").sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
  return (
    <div className="flex flex-col gap-3">
      {account && (
        <Link to={`/accounts/${account.id}`} className="flex flex-col gap-2 rounded-lg border border-border bg-card px-3.5 py-3">
          <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">Account</h2>
          <span className="flex items-center gap-2.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar text-[11px] font-bold text-white">{initialsOf(account.name)}</span>
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-[13px] font-semibold text-foreground">{account.name}</span>
              <span className="truncate text-xs text-muted-foreground">{[account.industry, account.state].filter(Boolean).join(" · ") || "—"}</span>
            </span>
          </span>
          {rollup && rollup.open_deals > 0 && (
            <span className="text-xs text-muted-foreground">
              <strong className="font-mono text-foreground">{formatINRCompact(rollup.open_value)}</strong> open on {rollup.open_deals} deal{rollup.open_deals === 1 ? "" : "s"} with them
            </span>
          )}
        </Link>
      )}

      <Card title={`On these deals · ${deals.length}`}>
        {deals.length === 0 && <span className="text-xs text-muted-foreground">Not on any deal yet.</span>}
        {deals.slice(0, 5).map((d) => (
          <div key={d.id} className="flex flex-col gap-1 border-t border-border/60 py-1.5 first:border-t-0">
            <div className="flex justify-between gap-2">
              <Link to={`/deals/${d.id}`} className="truncate text-[13px] font-semibold text-primary hover:underline">
                {d.name}
              </Link>
              <span className="font-mono text-xs">{formatINRCompact(d.amount)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-[11.5px] text-muted-foreground">
              <span>
                {d.deal_status === "OPEN" ? d.stage?.name || "Open" : d.deal_status === "WON" ? "Won" : "Lost"}
              </span>
              {d.buying_role ? <StatusPill tone={ROLE_TONE[d.buying_role] || "neutral"} dot={false}>{ROLE_LABEL[d.buying_role] || d.buying_role_label}</StatusPill> : <span>Main contact</span>}
            </div>
          </div>
        ))}
      </Card>

      <Duplicates type="CONTACT" id={contactId} canMerge={can.merge} onMerged={onMerged} />

      <Card title="Next step">
        {due.length === 0 && <span className="text-xs text-muted-foreground">Nothing scheduled.</span>}
        {due.slice(0, 3).map((t) => (
          <label key={t.id} className="flex items-center gap-2.5 rounded-lg border border-border px-2.5 py-2">
            <input type="checkbox" className="size-4 accent-primary" onChange={() => onDoneTask(t)} aria-label={`Mark "${t.title}" done`} />
            <span className="min-w-0 flex-1 truncate text-[13px]">{t.title}</span>
            <span className="font-mono text-xs text-primary">{when(t.due_at)}</span>
          </label>
        ))}
        {can.task && (
          <button type="button" onClick={onTask} className="pt-1 text-left text-[13px] font-medium text-primary hover:underline">
            + Add task
          </button>
        )}
      </Card>

      {colleagues.length > 0 && (
        <Card title="Also at this account">
          {colleagues.map((p) => (
            <Link key={p.id} to={`/contacts/${p.id}`} className="flex items-center gap-2 py-1">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[9.5px] font-bold text-muted-foreground">{initialsOf(fullName(p))}</span>
              <span className="flex-1 truncate text-[13px] text-foreground">{fullName(p)}</span>
              <span className="truncate text-xs text-muted-foreground">{p.job_title || ""}</span>
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}
